import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { integrationsApi } from '@/api/integrations';
import { unwrapError } from '@/api/client';
import type { IntegrationStatus, IntegrationType } from '@/api/types';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Field } from './forms/shared';
import { ElasticForm } from './forms/ElasticForm';
import { RagForm } from './forms/RagForm';
import { UpelaApiForm } from './forms/UpelaApiForm';
import { ZohoForm } from './forms/ZohoForm';
import { UgoForm } from './forms/UgoForm';
import { BigqueryForm } from './forms/BigqueryForm';
import { ShipperPortalForm } from './forms/ShipperPortalForm';
import { ApiConfigForm } from './forms/ApiConfigForm';
import { EmailForm } from './forms/EmailForm';

interface Props {
  instance: IntegrationStatus | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved: () => void;
}

export function EditInstanceDialog({ instance, open, onOpenChange, onSaved }: Props): JSX.Element {
  const { t } = useTranslation();
  const [label, setLabel] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [resetKey, setResetKey] = React.useState(0);

  const detail = useQuery({
    queryKey: ['integration-detail', instance?.instanceId],
    queryFn: () => integrationsApi.get(instance!.instanceId),
    enabled: open && !!instance,
    staleTime: 0,
  });

  React.useEffect(() => {
    if (open && instance) {
      setLabel(instance.label);
      setDescription(instance.description ?? '');
    }
  }, [open, instance]);

  const updateMutation = useMutation({
    mutationFn: (data: { label: string; description?: string; config: Record<string, unknown> }) =>
      integrationsApi.update(instance!.instanceId, data),
    onSuccess: () => {
      toast.success(t('integrations.savedToast', { title: label }));
      onSaved();
      onOpenChange(false);
    },
    onError: (err) => toast.error(unwrapError(err)),
  });

  const handleConfigSubmit = (config: Record<string, unknown>): void => {
    updateMutation.mutate({
      label: label.trim() || instance!.label,
      description: description.trim() || undefined,
      config,
    });
  };

  if (!instance) return <></>;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t('integrations.editTitle', { label: instance.label })}</DialogTitle>
          <DialogDescription>{t('integrations.dialogSubtitle')}</DialogDescription>
        </DialogHeader>

        <div className="grid gap-3 sm:grid-cols-2">
          <Field label={t('integrations.addInstance.labelField')} htmlFor="edit-label" required>
            <Input
              id="edit-label"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
            />
          </Field>
          <Field label={t('integrations.addInstance.descriptionField')} htmlFor="edit-desc">
            <Input
              id="edit-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </Field>
        </div>

        {detail.isLoading ? (
          <div className="flex items-center justify-center gap-2 py-12 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            {t('common.loading')}
          </div>
        ) : detail.error ? (
          <p className="py-6 text-sm text-destructive">{unwrapError(detail.error)}</p>
        ) : (
          <div key={resetKey} className="max-h-[50vh] overflow-y-auto rounded border bg-muted/10 p-4">
            <EditConfigForm
              type={instance.type}
              initial={detail.data?.config ?? null}
              onSubmit={handleConfigSubmit}
              submitting={updateMutation.isPending}
              onCancel={() => onOpenChange(false)}
              onReset={() => setResetKey((k) => k + 1)}
            />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function EditConfigForm({
  type,
  initial,
  onSubmit,
  submitting,
  onCancel,
  onReset,
}: {
  type: IntegrationType;
  initial: Record<string, unknown> | null;
  onSubmit: (config: Record<string, unknown>) => void;
  submitting: boolean;
  onCancel: () => void;
  onReset: () => void;
}): JSX.Element {
  const common = { submitting, onCancel, onReset };
  const wrappedSubmit = (config: unknown): void => {
    onSubmit(config as Record<string, unknown>);
  };

  switch (type) {
    case 'elastic':
      return <ElasticForm initial={initial as never} onSubmit={wrappedSubmit} {...common} />;
    case 'rag':
      return <RagForm initial={initial as never} onSubmit={wrappedSubmit} {...common} />;
    case 'upela-api':
      return <UpelaApiForm initial={initial as never} onSubmit={wrappedSubmit} {...common} />;
    case 'zoho':
      return <ZohoForm initial={initial as never} onSubmit={wrappedSubmit} {...common} />;
    case 'ugo':
      return <UgoForm initial={initial as never} onSubmit={wrappedSubmit} {...common} />;
    case 'bigquery':
      return <BigqueryForm initial={initial as never} onSubmit={wrappedSubmit} {...common} />;
    case 'shipper-portal':
      return <ShipperPortalForm initial={initial as never} onSubmit={wrappedSubmit} {...common} />;
    case 'api':
      return <ApiConfigForm initial={initial as never} onSubmit={wrappedSubmit} {...common} />;
    case 'email':
      return <EmailForm initial={initial as never} onSubmit={wrappedSubmit} {...common} />;
  }
}
